import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { toggle } from '@/features/sidebar';
import { IconButton } from '@/layouts/authenticated/components/top-navigation/components/icon-button';
import { servicesApi, servicesQuery } from '@/services/api';
import { servicesApi as untypedServicesApi } from '@/services/api/deprecated';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { authStore } from '@/services/auth/store';
import {
  setCopilotConfig,
  type APICopilotGetFileDownloadUrl,
  type APICopilotGetUploadSignature,
  type CopilotUploadSignatureResponse,
} from '@customerio/hydra-copilot';
import { SidebarChat } from './chat';
import { History } from './history';
import { useIsFullScreenAssistantPage } from './hooks';

setCopilotConfig({
  async startSession() {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await servicesApi.createCopilotSession({
      params: { accountId },
      body: {},
    });

    return {
      id: response.session_id,
      tools: response.tools,
    };
  },
  getUploadSignature: (async (
    payload: Parameters<APICopilotGetUploadSignature>[0],
  ): Promise<CopilotUploadSignatureResponse> => {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await servicesQuery.getCopilotFileUploadSignature.mutate({
      params: {
        accountId,
        sessionId: payload.id,
      },
      body: {
        size: payload.size,
        mime_type: payload.mime_type,
      },
    });

    return response.body as CopilotUploadSignatureResponse;
  }) satisfies APICopilotGetUploadSignature,
  getFileDownloadUrl: (async (payload: Parameters<APICopilotGetFileDownloadUrl>[0]) => {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await servicesApi.getCopilotFileDownloadUrl({
      params: {
        accountId,
        sessionId: payload.id,
        filename: payload.filename,
      },
    });

    return {
      url: response.url,
    };
  }) satisfies APICopilotGetFileDownloadUrl,
  async getSession(id) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await servicesApi.getCopilotSession({
      params: { accountId, sessionId: Number(id) },
    });

    return {
      meta: { name: response.name },
      messages: response.messages,
      tools: response.tools,
    };
  },
  async sendStreamingMessage({ onMessage, id, signal, ...payload }) {
    let currentLine = -1;
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    await untypedServicesApi.post(
      `/accounts/${accountId}/copilot/${id}`,
      {
        environment_id: getWorkspaceIdFromCurrentUrl(),
        stream: true,
        ...payload,
      },
      {
        signal,
        onDownloadProgress: (progressEvent) => {
          const response = progressEvent.event.currentTarget.response;
          const lines = response.split('\n');

          for (let index = 0; index < lines.length; index++) {
            const line = lines[index].trim();
            if (line.length === 0) {
              continue;
            }

            // skip lines that have already been processed
            if (index <= currentLine) {
              continue;
            }

            // we are now processing this line
            currentLine = index;

            try {
              onMessage(JSON.parse(line));
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Error parsing message from server:', line, error);
              // TODO: If we can't parse the message, we should kill this exchange entirely
              // This can happen if the server sends an incomplete message
            }
          }
        },
      },
    );
  },
  async stopExchange({ id }) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    await untypedServicesApi.post(`/accounts/${accountId}/copilot/${id}/stop`, {});
  },
  async endSession() {
    // noop
  },
  async generate(payload) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const { data } = await untypedServicesApi.post(`/accounts/${accountId}/copilot_generate`, {
      ...payload,
      environment_id: getWorkspaceIdFromCurrentUrl(),
    });

    return data;
  },
});

export function Copilot(props?: { isNewLayout?: boolean }) {
  const isFullScreenAssistantPage = useIsFullScreenAssistantPage();
  const isV2 = useFeatureFlag(AccountFeatureFlag.AiAssistantV2);

  return (
    <>
      {isV2 ? null : (
        <IconButton
          label="AI Assistant"
          tooltip={isFullScreenAssistantPage ? 'AI chat not available when on the assistant page' : 'Assistant'}
          icon="chat"
          onClick={() => {
            toggle('copilot');
          }}
          isDisabled={isFullScreenAssistantPage}
          isNewLayout={props?.isNewLayout}
        />
      )}
      <History />
      <SidebarChat />
    </>
  );
}

export { CopilotActions, CopilotInput, CopilotMessages, CopilotName, CopilotSupportButton } from './chat';
export { CopilotSessionProvider, useCopilotChatContext, useCopilotSessionContext } from './components/session-provider';
export { Tour } from './components/tour';
export { AssistantFooter } from './components/assistant-footer';
export { useIsFullScreenAssistantPage } from './hooks';
